import React, {useState, useEffect} from 'react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Box,
    Text,
    Radio,
    RadioGroup,
    Stack as ChakraStack,
    HStack,
    useMultiStyleConfig,
    VStack,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {AdyenCheckoutProvider, useAdyenCheckout} from '../../vendors/adyen'
import {brandIcons} from '../../pages/account/payment-methods/partials/StoredCard'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {CheckoutProvider} from '../../pages/checkout/util/checkout-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {MRT_API_BASEPATH} from '../../ssr/constants'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useHistory} from 'react-router-dom'
import {remCalc} from '../../theme/_utils'
import LoadingSpinner from '../loading-spinner'
import {CloseIcon} from '../icons'

const SubscriptionEditPaymentModalContent = ({
    isOpen,
    onClose,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    currentPayment,
    updateRender,
    subscriptionId,
    onSuccess,
}) => {
    const {adyenPaymentMethods} = useAdyenCheckout()
    const [selectedPayment, setSelectedPayment] = useState('current')
    const [storedMethods, setStoredMethods] = useState([])
    const appOrigin = useOrigin(false)
    const [message, setMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const history = useHistory()
    const {locale} = useMultiSite()
    const localeId = locale?.alias
    const styles = useMultiStyleConfig('Subscriptions')
    const {getTokenWhenReady} = useAccessToken()
    const {data: customer} = useCurrentCustomer()

    useEffect(() => {
        if (adyenPaymentMethods?.storedPaymentMethods) {
            setStoredMethods(adyenPaymentMethods.storedPaymentMethods)
        }
    }, [adyenPaymentMethods])

    // Always preselect the first card (default behaviour)
    useEffect(() => {
        if (storedMethods.length > 0) {
            setSelectedPayment(storedMethods[0].id)
        } else {
            setSelectedPayment('current')
        }
    }, [storedMethods])

    // Helper to get selected payment method details
    const getSelectedPaymentDetails = () => {
        const method = storedMethods.find((m) => m.id === selectedPayment)
        if (method) {
            return {
                PaymentAdditionalInfo__c: method.lastFour, // TODO understand what this is
                PaymentExpiryDate__c: `${method.expiryMonth}/${method.expiryYear}`,
                PaymentMethod__c: method.brand?.toUpperCase(),
                PaymentMethodOwner__c: method.holderName,
                PaymentTokenId__c: method.id,
            }
        }
        return {}
    }

    // Handle the confirm edit payment method action
    const handleEditPaymentMethod = async () => {
        try {
            setIsLoading(true)
            const token = await getTokenWhenReady()

            const paymentDetails = getSelectedPaymentDetails()

            const patchPayload = {
                subscriptionId,
                allOrNone: true,
                compositeRequest: [
                    {
                        method: 'PATCH',
                        url: `/services/data/v59.0/sobjects/Subscription__c/${subscriptionId}`,
                        referenceId: 'refAccount',
                        body: paymentDetails,
                    },
                ],
            }

            /* Not needed for now, but can be used to update preferred payment token eventually
            // Update preferred payment token for the customer
            let selectedCardId =
                selectedPayment === 'current' ? currentPayment?.paymentTokenId : selectedPayment
            if (selectedCardId && customer?.customerId) {
                await updateCustomer
                    .mutateAsync({
                        body: {c_preferredPaymentToken: selectedCardId},
                        parameters: {customerId: customer.customerId},
                    })
                    .catch((error) => {
                        console.error('error', error)
                    })
            }
            */

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/update-subscription-payment-method-oms`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        customerId: customer?.customerId,
                        'x-commercecloud-token': `Bearer ${token}`,
                    },
                    body: JSON.stringify(patchPayload),
                },
            )

            const result = await response.json()
            if (result.success) {
                setMessage('Payment method updated successfully')
                if (typeof updateRender === 'function') updateRender()
                onClose()
                if (typeof onSuccess === 'function') {
                    onSuccess()
                }
            } else {
                setMessage('Error during payment method update: ' + result.error)
            }
        } catch (error) {
            console.error('Error during payment method update:', error)
            setMessage('Error during payment method update: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                onClose()
            }}
            size="lg"
            isCentered
        >
            <ModalOverlay />
            <ModalContent padding="40px 24px">
                <ModalHeader {...styles.subscriptionEditHeaderContent}>
                    <FormattedMessage
                        id="subscription_edit_payment.title"
                        defaultMessage="Edit Payment Method"
                    />
                </ModalHeader>
                <ModalCloseButton {...styles.modalClose} top={remCalc(36)}>
                    <CloseIcon {...styles.modalCloseIcon} />
                </ModalCloseButton>
                <ModalBody {...styles.subscriptionEditBodyContent}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <Text mb={4} {...styles.infoValue}>
                                <FormattedMessage
                                    id="subscription_edit_payment.desc"
                                    defaultMessage="Edit the payment method. The new credit or debit card will be used starting from the next order."
                                />
                            </Text>
                            <RadioGroup value={selectedPayment} onChange={setSelectedPayment}>
                                <ChakraStack gap={4}>
                                    {/* Stored methods cards */}
                                    {storedMethods?.map((method) => (
                                        <Box
                                            as="label"
                                            key={method.id}
                                            {...styles.paymentModifyCardTile}
                                        >
                                            <Radio
                                                value={method.id}
                                                mr={4}
                                                {...styles.subscriptionEditRadio}
                                            >
                                                <Text as="span" fontWeight="bold">
                                                    <FormattedMessage
                                                        id="subscription_edit_payment.use_this"
                                                        defaultMessage="Use this card"
                                                    />
                                                </Text>
                                            </Radio>
                                            <VStack {...styles.paymentModifyOuterStack}>
                                                <HStack {...styles.paymentModifyIconAndEndsWith}>
                                                    {brandIcons[method.brand?.toLowerCase?.()] ||
                                                        null}
                                                    <Text fontWeight="bold">
                                                        {method.name || method.brand}{' '}
                                                        <FormattedMessage
                                                            id="subscription_edit_payment.ends_with"
                                                            defaultMessage="ends with {lastFour}"
                                                            values={{lastFour: method.lastFour}}
                                                        />
                                                    </Text>
                                                </HStack>
                                                <VStack {...styles.paymentModifyInnerStack}>
                                                    <Text color="gray.600" fontSize="sm">
                                                        <FormattedMessage
                                                            id="payment_methods.card.name"
                                                            defaultMessage="Name of the card"
                                                        />
                                                    </Text>
                                                    <Text>{method.holderName}</Text>
                                                    <Text color="gray.600" fontSize="sm">
                                                        <FormattedMessage
                                                            id="payment_methods.card.expDate"
                                                            defaultMessage="Expiration date"
                                                        />
                                                    </Text>
                                                    <Text>
                                                        {method.expiryMonth}/{method.expiryYear}
                                                    </Text>
                                                </VStack>
                                            </VStack>
                                        </Box>
                                    ))}
                                </ChakraStack>
                            </RadioGroup>
                            {message && (
                                <Text color="red.500" mt={4}>
                                    {message}
                                </Text>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    paddingBottom="0px"
                    paddingX={0}
                >
                    {!isLoading && (
                        <Button
                            variant="secondary"
                            width="100%"
                            mb={4}
                            onClick={() => history.push(`/${localeId}/account/payment-methods`)}
                        >
                            <FormattedMessage
                                id="subscription_edit_payment.add_new"
                                defaultMessage="Add new payment method"
                            />
                        </Button>
                    )}
                    {!isLoading && (
                        <Button width="100%" variant="primary" onClick={handleEditPaymentMethod}>
                            <FormattedMessage
                                id="subscription_edit_payment.confirm"
                                defaultMessage="Confirm"
                            />
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

SubscriptionEditPaymentModalContent.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentPayment: PropTypes.shape({
        method: PropTypes.string,
        additionalInfo: PropTypes.string,
        owner: PropTypes.string,
        expiryDate: PropTypes.string,
        paymentTokenId: PropTypes.string,
    }),
    updateRender: PropTypes.func,
    subscriptionId: PropTypes.string.isRequired,
    onSuccess: PropTypes.func, // add this
}

const SubscriptionEditPaymentModal = (props) => {
    const {getTokenWhenReady} = useAccessToken()
    const {data: customer} = useCurrentCustomer()
    const {data: basket, isLoading} = useCurrentBasket()
    const createBasket = useShopperBasketsMutation('createBasket')
    const navigate = useNavigation()
    const {locale, site} = useMultiSite()

    const [authToken, setAuthToken] = useState()
    const [updateRender, setUpdateRender] = useState(0)

    useEffect(() => {
        if (!isLoading && !basket)
            createBasket.mutateAsync({
                body: {},
            })
    }, [basket, isLoading])

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }
        getToken()
    }, [])

    if (!authToken) {
        // Optionally show a skeleton or loader here
        return null
    }

    const updateRenderCallback = () => {
        setUpdateRender(updateRender + 1)
    }

    return (
        <AdyenCheckoutProvider
            authToken={authToken}
            customerId={customer.customerId}
            isCustomerRegistered={customer.isRegistered}
            locale={locale}
            site={site}
            basket={basket}
            navigate={navigate}
        >
            <CheckoutProvider>
                <SubscriptionEditPaymentModalContent
                    {...props}
                    subscriptionId={props.subscriptionId}
                    updateRender={updateRenderCallback}
                    onSuccess={props.onSuccess}
                />
            </CheckoutProvider>
        </AdyenCheckoutProvider>
    )
}

SubscriptionEditPaymentModal.propTypes = SubscriptionEditPaymentModalContent.propTypes

export default SubscriptionEditPaymentModal
